import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';

export interface OverlayRouteParams {
  organizerId?: number;
  deviceId?: number;
  token?: string;
  /**
   * Готовый для показа текст: чего в адресе не хватает. `undefined`, когда всё на месте.
   * Оверлей и панель открываются по ссылке, вставленной в OBS или в адресную строку,
   * поэтому битую ссылку надо объяснить, а не падать исключением на белом экране.
   */
  error?: { title: string; subtitle: string };
}

/** Разбор адреса `organizer/:organizerId/device/:deviceId/...?token=` без исключений. */
function useOverlayRouteParams(): OverlayRouteParams {
  const { organizerId: organizerIdParam, deviceId: deviceIdParam } = useParams();
  const { t } = useLingui();
  const token = useQueryParam('token')[0] as string | undefined;

  return useMemo(() => {
    const copyAgain = t`Скопируйте ссылку заново: раздел «Устройства» → строка нужного устройства → «Копировать ссылку».`;
    const organizerId = organizerIdParam ? Number(organizerIdParam) : undefined;
    const deviceId = deviceIdParam ? Number(deviceIdParam) : undefined;

    if (organizerId === undefined || isNaN(organizerId)) {
      return { token, error: { title: t`В ссылке не указан организатор`, subtitle: copyAgain } };
    }

    if (deviceId === undefined || isNaN(deviceId)) {
      return { organizerId, token, error: { title: t`В ссылке не указано устройство`, subtitle: copyAgain } };
    }

    if (!token) {
      return {
        organizerId,
        deviceId,
        error: { title: t`В ссылке нет токена авторизации`, subtitle: copyAgain },
      };
    }

    return { organizerId, deviceId, token };
  }, [organizerIdParam, deviceIdParam, token, t]);
}

export default useOverlayRouteParams;
